import { createSelector } from 'reselect';

import type { RootState } from '~/app/rootReducer';
import {
  SearchConfigType,
  SessionConfigType,
} from '~/common/constants/common.constant';
import {
  BDRequestType,
  type OperationStatus,
} from '~/common/models/apis/apiResponse.model';
import {
  type ApiAssetType,
  type Asset,
  AssetIgnitionStatus,
  type AssetMapItem,
  AssetReportType,
  type AssetTrip,
  type AssetType,
  AssetTypeModel,
  type AssignAssetItem,
  type GearStatus,
  type TelemetryStatus,
} from '~/common/models/asset.model';
import type {
  AdvisorVehicleListItem,
  AssetReportItem,
  PendingVehicleItem,
} from '~/common/models/asset-report.model';
import type { ContinuationCache } from '~/common/models/common.model';
import {
  countArrayAttributeValues,
  createSafeSessionId,
  isUnsafeObjectKey,
} from '~/common/utils/common.utils';
import {
  getOperationStatus,
  IDLE_OPERATION_STATUS,
  selectSessionConfigById,
} from '~/common/utils/store/selector.helper';
import { PeripheralVehiclesConfig } from '~/routes/organizations/devices/peripheral/config/constants/state';
import type {
  AssetRemoteCommandRequestType,
  RemoteCommandPowerIgnitionStatus,
} from '~/routes/organizations/vehicles/details/components/remoteCommands/remoteCommands.constants';
import { NotificationSettingsConfigType } from '~/routes/settings/settings.constant';
import {
  type AssetComplianceSession,
  type AssetEventListSession,
  type AssetListFilterCriteria,
  type AssetListSession,
  type AssetListView,
  AssetReportConfigType,
  AssetRequestType,
  DEFAULT_ITEMS_PER_PAGE,
  FIRST_PAGE,
  type SelectedVehicleIds,
  selectAllAssetEvents,
  selectAllAssetReportItems,
  selectAllAssets,
  selectAllSelectedAssets,
  selectAssetById,
  selectAssetsState,
} from './assetsSlice';

import { DEFAULT_SORT_ORDER } from './list/utils/vehicleListSort.utils';
import {
  mapAssetToLocationWithId,
  mapAssetToMapItem,
} from './mappers/asset.mappers';
import {
  getMappedAssetsByClass,
  getTripsFromAssetEvents,
} from './utils/assets.utils';

export const selectAssetsSessionConfigs = createSelector(
  selectAssetsState,
  (assetsState) => assetsState.sessionConfigs
);
export const selectAdvisorVehicleSessionConfigs = createSelector(
  selectAssetsState,
  (assetsState) => assetsState.sessionConfigs
);
export const selectAssetDetailsSessionConfigs = createSelector(
  selectAssetsSessionConfigs,
  (sessionConfigs) => sessionConfigs[SessionConfigType.DETAIL_VIEW]
);

export const selectAssetListSessionConfigs = createSelector(
  selectAssetsSessionConfigs,
  (sessionConfigs) => sessionConfigs[SessionConfigType.LIST_VIEW]
);

export const selectPeripheralVehicleListSessionConfigs = createSelector(
  selectAssetsSessionConfigs,
  (sessionConfigs) => sessionConfigs[PeripheralVehiclesConfig.LIST_VIEW]
);

export const selectPendingVehicleListSessionConfigs = createSelector(
  selectAssetsSessionConfigs,
  (sessionConfigs) => sessionConfigs[AssetReportConfigType.PENDING_VIEW]
);

export const selectAssetSearchSessionConfigs = createSelector(
  selectAssetsSessionConfigs,
  (sessionConfigs) => sessionConfigs[SearchConfigType.SEARCH_VIEW]
);

export const selectVehicleNotificationSessionConfigs = createSelector(
  selectAssetsSessionConfigs,
  (sessionConfigs) => sessionConfigs[NotificationSettingsConfigType.ADD_VIEW]
);

export const makeVehicleNotificationSettingsSessionSelector = (
  sessionId: string
): ((state: RootState) => Partial<AssetListSession> | undefined) =>
  createSelector(
    selectVehicleNotificationSessionConfigs,
    (reportSessions) => reportSessions?.[sessionId]
  );

export const selectAssetReportSessionConfigs = createSelector(
  selectAssetsSessionConfigs,
  (sessionConfigs) => sessionConfigs[AssetReportConfigType.REPORT_VIEW]
);

export const selectAssetComplianceSessionConfigs = createSelector(
  selectAssetsSessionConfigs,
  (sessionConfigs) => sessionConfigs[AssetReportConfigType.COMPLIANCE_VIEW]
);

export const selectAllAssetsByClass = createSelector(
  selectAllAssets,
  (assets: Asset[]) => getMappedAssetsByClass(assets)
);

export const makeAssetDetailsRecallSelector = (assetId: string | undefined) => {
  return createSelector(selectAssetDetailsSessionConfigs, (config) => {
    const key = createSafeSessionId(assetId);
    // Guard on the validated key matching the input: if createSafeSessionId fell
    // back (unsafe/missing id), key !== assetId and we must not read default-session data.
    const isSafeId =
      Boolean(assetId) &&
      !isUnsafeObjectKey(assetId as string) &&
      key === assetId;
    return isSafeId && config?.[key]?.recalls?.recallItems !== null
      ? config?.[key]?.recalls?.recallItems
      : null;
  });
};
export const makeAssetDetailsRecallOperatorSelector = (
  assetId: string | undefined
) => {
  return createSelector(selectAssetDetailsSessionConfigs, (config) => {
    const key = createSafeSessionId(assetId);
    // Guard on the validated key matching the input: if createSafeSessionId fell
    // back (unsafe/missing id), key !== assetId and we must not read default-session data.
    const isSafeId =
      Boolean(assetId) &&
      !isUnsafeObjectKey(assetId as string) &&
      key === assetId;
    return (
      (isSafeId && config?.[key]?.recalls?.operationStatus) ||
      IDLE_OPERATION_STATUS
    );
  });
};

export const makeSelectAssetListSessionConfigById = (
  id: string
): ((state: RootState) => Partial<AssetListSession> | undefined) =>
  createSelector(
    selectAssetListSessionConfigs,
    (listSessionConfigs) => listSessionConfigs?.[id]
  );
export const makeSelectAssetListSelectedRowIndicesSelector = (
  sessionId: string
): ((state: RootState) => number[] | undefined) =>
  createSelector(
    makeSelectAssetReportSessionSelector(sessionId),
    (session) => session?.selectedIndices
  );

export const makeSelectAssetListSelectedAssets = (
  sessionId: string
): ((state: RootState) => AssetReportItem[]) => {
  const selectAssetReportSession =
    makeSelectAssetReportSessionSelector(sessionId);
  return createSelector(
    (state: RootState) => {
      const session = selectAssetReportSession(state);
      const selectedAssetsState =
        session && typeof session === 'object' && session.selectedAssets
          ? selectAllSelectedAssets(session.selectedAssets)
          : [];
      return selectedAssetsState;
    },
    (assets) => {
      return assets;
    }
  );
};

export const makeSelectAssetListAllSelectedConfig = (
  sessionId: string
): ((state: RootState) => boolean | undefined) => {
  const selectAssetReportSession =
    makeSelectAssetReportSessionSelector(sessionId);
  return createSelector(
    selectAssetReportSession,
    (session) => session?.allSelected
  );
};

export const makeAssetListViewSessionConfigSelector = <
  K extends keyof AssetListSession,
>(
  id: string,
  configKey: K
): ((state: RootState) => Partial<AssetListSession>[K]) =>
  createSelector(
    makeSelectAssetListSessionConfigById(id),
    (listSessionConfig) => listSessionConfig?.[configKey]
  );

export const makeSelectAssetListViewActiveTab = (
  id: string
): ((state: RootState) => AssetType | undefined) =>
  makeAssetListViewSessionConfigSelector(id, 'activeTab');

export const makeSelectAssetListViewActiveTabSessionConfig = <
  K extends keyof AssetListView,
>(
  id: string,
  configKey: K
): ((state: RootState) => AssetListView[K] | undefined) =>
  createSelector(
    (state: RootState) => {
      const listViewSessionConfig = selectAssetListSessionConfigs(state)?.[id];
      return (
        (!!listViewSessionConfig &&
          !!listViewSessionConfig?.activeTab &&
          listViewSessionConfig[listViewSessionConfig.activeTab]) ||
        undefined
      );
    },
    (listViewTabConfig) => listViewTabConfig?.[configKey] || undefined
  );

export const makeSelectSelectedAssets = (
  id: string,
  allowEmpty = true
): ((state: RootState) => AssignAssetItem[]) => {
  const selectSelectedIndices = makeSelectAssetListSelectedIndices(id);
  const selectActiveTab = makeSelectAssetListViewActiveTab(id);
  return createSelector(
    selectAllAssetsByClass,
    selectActiveTab,
    selectSelectedIndices,
    (assetsByClass, activeTab, selectedIndices) => {
      if (assetsByClass && activeTab && selectedIndices) {
        const assets =
          assetsByClass[activeTab] ||
          (allowEmpty && []) ||
          assetsByClass['all'];
        const selectedAssets = selectedIndices
          .map((idx) => assets[idx])
          .filter((asset) => asset !== undefined);
        return selectedAssets;
      }
      return [];
    }
  );
};

export const makeSelectAssetListSelectedIndices = (
  id: string
): ((state: RootState) => number[] | undefined) =>
  makeSelectAssetListViewActiveTabSessionConfig(id, 'selectedIndices');

/**
 * Create memoized selector for assets with the specified `AssetType`.
 * By default will return all assets of any type if none exist with specified `AssetType`
 * @param assetType `AssetType` to match
 * @param allowEmpty allow returning empty array even if other assets exist; defaults to `false`
 * @returns assets matching provided `AssetType`, all assets, or empty array
 */
export const makeSelectAssetsByTab = (
  assetType: AssetType | 'all',
  allowEmpty = false
): ((state: RootState) => Asset[]) =>
  createSelector(selectAllAssetsByClass, (assetsByClass) => {
    return (
      assetsByClass[assetType] || (allowEmpty && assetsByClass['all']) || []
    );
  });

export const makeSelectActiveTabAssets = (
  sessionId: string
): ((state: RootState) => Asset[]) =>
  createSelector(
    selectAllAssetsByClass,
    makeSelectAssetListViewActiveTab(sessionId),
    (assetsByClass, activeTab) =>
      (!!assetsByClass && !!activeTab && assetsByClass[activeTab]) || []
  );

export const selectAssetsByType = (
  assetType: AssetType
): ((state: RootState) => Asset[]) =>
  createSelector(
    selectAllAssetsByClass,
    (assetsByClass) => assetsByClass?.[assetType] || []
  );

export const makeSelectAssetLocationById = (assetId: string) =>
  createSelector(
    (state: RootState) => selectAssetById(state, assetId),
    (asset) => mapAssetToLocationWithId(asset)
  );

export const makeSelectAssetLocationsByIds = (assetIds?: string[]) =>
  createSelector(selectAllAssets, (assets) =>
    assets
      ?.filter(({ id }) => assetIds?.find((assetId) => assetId === id))
      .map((asset) => mapAssetToLocationWithId(asset))
  );

export const selectAssetEventsAsTrips: (
  state: RootState
) => AssetTrip[] | undefined = createSelector(selectAllAssetEvents, (events) =>
  getTripsFromAssetEvents(events)
);

export const makeAssetEventSelectionChangedSelector = (assetId: string) =>
  createSelector(
    makeSelectAssetEventSession(assetId),
    (assetSession) => assetSession?.selectedPage === undefined
  );
export const makeSelectAssetEventSession = (assetId: string) =>
  createSelector(
    (state: RootState) =>
      selectSessionConfigById(state.assets, SessionConfigType.DETAIL_VIEW),
    (session) => session?.[assetId] as Partial<AssetEventListSession>
  );

export const makeSelectAssetDetailSessionSelectedTripId = (assetId: string) =>
  createSelector(
    makeSelectAssetEventSession(assetId),
    (session) => session?.selectedTripId
  );

export const makeAssetActiveSectionSelector = (assetId: string) =>
  createSelector(
    makeSelectAssetEventSession(assetId),
    (session) => session?.activeSection
  );

export const makeSelectAssetDetailSessionSelectedEventId = (assetId: string) =>
  createSelector(
    makeSelectAssetEventSession(assetId),
    (session) => session?.selectedEvent
  );

export const makeAssetDetailSessionSelectedPageSelector = (assetId: string) =>
  createSelector(
    makeSelectAssetEventSession(assetId),
    (session) => session?.selectedPage || FIRST_PAGE
  );

export const makeAssetDetailSessionRowsPerPageSelector = (assetId: string) =>
  createSelector(
    makeSelectAssetEventSession(assetId),
    (session) => session?.rowsPerPage || DEFAULT_ITEMS_PER_PAGE
  );

export const makeSelectAssetDetailSessionSelectedDate = (assetId: string) =>
  createSelector(
    makeSelectAssetEventSession(assetId),
    ({ selectedDate }) => selectedDate
  );

export const makeSelectAssetDetailSessionSelectedStartDate = (
  assetId: string
) =>
  createSelector(
    makeSelectAssetEventSession(assetId),
    ({ selectedStartDate }) => selectedStartDate
  );

export const makeSelectAssetDetailSessionSelectedEndDate = (assetId: string) =>
  createSelector(
    makeSelectAssetEventSession(assetId),
    ({ selectedEndDate }) => selectedEndDate
  );

export const makeSelectAssetDetailSessionSelectedStartTime = (
  assetId: string
) =>
  createSelector(
    makeSelectAssetEventSession(assetId),
    ({ selectedStartTime }) => selectedStartTime
  );

export const makeSelectAssetDetailSessionSelectedEndTime = (assetId: string) =>
  createSelector(
    makeSelectAssetEventSession(assetId),
    ({ selectedEndTime }) => selectedEndTime
  );

export const selectAssetStartDate = createSelector(
  (state: RootState) => state.assets,
  ({ startDate }) => startDate
);

export const selectAssetEndDate = createSelector(
  (state: RootState) => state.assets,
  ({ endDate }) => endDate
);

export const selectAssetLocationsStatus = createSelector(
  (state: RootState) => state.assets,
  (assets) => getOperationStatus(assets, AssetRequestType.GET_ASSET_LOCATION)
);

export const selectAssetEventsStatus = createSelector(
  (state: RootState) => state.assets,
  (assets) => getOperationStatus(assets, AssetRequestType.GET_ASSET_HISTORY)
);

export const makeSelectAssetEventsTrip = (tripId: string) =>
  createSelector(selectAssetEventsAsTrips, (trips) =>
    trips?.find((trip) => trip.id === tripId)
  );
export const makeSelectAssetEvents = (eventId: string) =>
  createSelector(selectAllAssetEvents, (events) =>
    events?.find((event: { id: string }) => event.id === eventId)
  );

export const allAssetsOperationStatusSelector = createSelector(
  selectAssetsState,
  (assetsState) => getOperationStatus(assetsState, BDRequestType.GET_ALL)
);

export const selectAssetOperations = createSelector(
  selectAssetsState,
  (assetsState) => assetsState.operations
);

export const makeSelectAssetOperationStatus = (
  operation: AssetRequestType | BDRequestType
): ((state: RootState) => OperationStatus) =>
  createSelector(
    selectAssetOperations,
    (operations) => operations?.[operation] || IDLE_OPERATION_STATUS
  );

export const selectAllAssetsOperation = makeSelectAssetOperationStatus(
  BDRequestType.GET_ALL
);
export const selectAssetByIdOperation = makeSelectAssetOperationStatus(
  BDRequestType.GET_BY_ID
);

export const makeAssetSessionFaults = (assetId: string) =>
  createSelector(
    makeSelectAssetEventSession(assetId),
    (session) => session?.faults
  );

/**
 * Create selector for asset DTCs with exclusions
 * @see https://dev.azure.com/BrightDrop/BrightDrop/_workitems/edit/25745
 */
export const makeSelectAssetFaults = (
  assetId: string,
  hasGlobalRole: boolean,
  severityExclusions = ['info']
) => {
  return createSelector(
    (state: RootState) => selectAssetById(state, assetId),
    makeAssetSessionFaults(assetId),
    (asset, faultsData) => {
      if (hasGlobalRole) {
        return faultsData || [];
      }
      if (asset?.type.model === AssetTypeModel.ZEVO) {
        return (faultsData || []).filter(
          (fault) => fault.severity && fault.mil === true
        );
      }
      return (faultsData || []).filter(
        (fault) =>
          fault.severity && !severityExclusions.includes(fault.severity)
      );
    }
  );
};

export const makeAssetFaultsCountSelector = (
  assetId: string,
  hasGlobalRole: boolean
) =>
  createSelector(
    makeSelectAssetFaults(assetId, hasGlobalRole),
    (faults) => faults?.length || 0
  );

export const makeAssetDiagnosticCodesSelector = (assetId: string) =>
  createSelector(
    (state: RootState) => selectAssetById(state, assetId),
    (asset) => asset?.status?.diagnostics?.value
  );

export const makeSelectAssetClass = (assetId: string) => {
  return createSelector(
    (state: RootState) => selectAssetById(state, assetId)?.type,
    (assetType) => assetType?.class
  );
};

export const makeSelectAssetOrgIdSelector = (assetId: string) =>
  createSelector(
    (state: RootState) => selectAssetById(state, assetId),
    (asset) => asset?.organization?.id
  );

export const selectAllAssetsAsMapItems = createSelector(
  selectAllAssets,
  (assets) => assets?.map((asset) => mapAssetToMapItem(asset))
);

export const selectCachedAssetAsMapItem = (assetId: string) =>
  createSelector(selectAllAssetsAsMapItems, (items) =>
    items?.find(({ id }) => id === assetId)
  );

export const makeSelectAssetAsMapItem = (
  assetId: string
): ((state: RootState) => AssetMapItem | undefined) =>
  createSelector(
    (state: RootState) => selectAssetById(state, assetId),
    (asset) => (asset ? mapAssetToMapItem(asset) : undefined)
  );

export const makeSelectAssetListViewSessionSelector = (
  viewType: SessionConfigType.LIST_VIEW,
  sessionId: string
): ((state: RootState) => Partial<AssetListSession> | undefined) =>
  createSelector(
    (state: RootState) =>
      sessionId ? state.assets.sessionConfigs?.[viewType] : undefined,
    (session) => session?.[sessionId]
  );

export const makeSelectSearchFilterValue = <
  K extends keyof Partial<AssetListSession>,
>(
  viewType: SessionConfigType.LIST_VIEW,
  sessionId: string,
  type: K
): ((state: RootState) => Partial<AssetListSession>[K] | undefined) =>
  createSelector(
    makeSelectAssetListViewSessionSelector(viewType, sessionId),
    (session) => session?.[type]
  );

export const makeSelectAssetReportSessionSelector = (
  sessionId: string
): ((state: RootState) => Partial<AssetListSession> | undefined) =>
  createSelector(selectAssetListSessionConfigs, (reportSessions) => {
    if (reportSessions && sessionId in reportSessions) {
      return reportSessions[sessionId];
    }
    return undefined;
  });

export const makeSelectPeripheralAssetReportSessionSelector = (
  sessionId: string
): ((state: RootState) => Partial<AssetListSession> | undefined) =>
  createSelector(
    selectPeripheralVehicleListSessionConfigs,
    (reportSessions) => reportSessions?.[sessionId]
  );

export const makeSelectPendingVehicleSessionSelector = (
  sessionId: string
): ((state: RootState) => Partial<AssetListSession> | undefined) =>
  createSelector(
    selectPendingVehicleListSessionConfigs,
    (reportSessions) => reportSessions?.[sessionId]
  );

export const makeSelectAssetComplianceSessionSelector = (
  sessionId: string
): ((state: RootState) => Partial<AssetComplianceSession> | undefined) =>
  createSelector(
    selectAssetComplianceSessionConfigs,
    (complianceSessions) => complianceSessions?.[sessionId]
  );

export const makeSelectAssetSearchSessionSelector = (
  sessionId: string
): ((state: RootState) => Partial<AssetListSession> | undefined) =>
  createSelector(
    selectAssetSearchSessionConfigs,
    (assetSessions) => assetSessions?.[sessionId]
  );

export const makeReportFilterCriteriaSelector = (
  sessionId: string
): ((state: RootState) => AssetListFilterCriteria | undefined) =>
  createSelector(
    makeSelectAssetReportSessionSelector(sessionId),
    (config) => config?.filterType
  );

export const makeReportFilterCriteriaCountSelector = (
  sessionId: string
): ((state: RootState) => number | undefined) =>
  createSelector(
    makeReportFilterCriteriaSelector(sessionId),
    (filterCriteria) => countArrayAttributeValues(filterCriteria)
  );

export const makeReportFilterModalOpenSelector = (
  sessionId: string
): ((state: RootState) => boolean | undefined) =>
  createSelector(
    makeSelectAssetReportSessionSelector(sessionId),
    (config) => config?.filterModalOpen
  );

export const makeSelectActiveAssetReportSelector = (
  sessionId: string
): ((state: RootState) => AssetReportType) =>
  createSelector(
    makeSelectAssetReportSessionSelector(sessionId),
    (config) => config?.activeReport || AssetReportType.FLEET_READINESS
  );

export const makeSelectReportCountSelector = (
  sessionId: string
): ((state: RootState) => number) =>
  createSelector(
    makeSelectAssetReportSessionSelector(sessionId),
    (config) => config?.count || 0
  );
export const makeSelectPeripheralVehicleCountSelector = (
  sessionId: string
): ((state: RootState) => number | undefined) =>
  createSelector(
    makeSelectPeripheralAssetReportSessionSelector(sessionId),
    (config) => config?.count
  );
export const makeSelectPendingVehicleCountSelector = (
  sessionId: string
): ((state: RootState) => number | undefined) =>
  createSelector(
    makeSelectPendingVehicleSessionSelector(sessionId),
    (config) => config?.count
  );

export const makeSelectReportPageNumberSelector = (
  sessionId: string
): ((state: RootState) => number) =>
  createSelector(
    makeSelectAssetReportSessionSelector(sessionId),
    (config) => config?.page || 0
  );

export const makeSelectReportSortOrderSelector = (
  sessionId: string
): ((state: RootState) => { name: string; direction: 'asc' | 'desc' }) =>
  createSelector(
    makeSelectAssetReportSessionSelector(sessionId),
    (config) => config?.sortOrder || DEFAULT_SORT_ORDER
  );

export const makeWithoutOrgAssignmentSwitchSelector = (
  sessionId: string
): ((state: RootState) => boolean) =>
  createSelector(
    makeSelectAssetReportSessionSelector(sessionId),
    (config) => !!config?.withoutOrganizationAssignment
  );

export const makeSelectReportOperationStatusSelector = (
  sessionId: string
): ((state: RootState) => OperationStatus) =>
  createSelector(
    makeSelectAssetReportSessionSelector(sessionId),
    (config) => config?.operationStatus || IDLE_OPERATION_STATUS
  );

export const makeSelectPeripheralReportOperationStatusSelector = (
  sessionId: string
): ((state: RootState) => OperationStatus) =>
  createSelector(
    makeSelectPeripheralAssetReportSessionSelector(sessionId),
    (config) => config?.operationStatus || IDLE_OPERATION_STATUS
  );

export const makeSelectPendingVehicleOperationStatusSelector = (
  sessionId: string
): ((state: RootState) => OperationStatus) =>
  createSelector(
    makeSelectPendingVehicleSessionSelector(sessionId),
    (config) => config?.operationStatus || IDLE_OPERATION_STATUS
  );

export const makeSelectAssetSearchCountSelector = (
  sessionId: string
): ((state: RootState) => number | undefined) =>
  createSelector(
    makeSelectAssetSearchSessionSelector(sessionId),
    (config) => config?.count
  );

export const makeSelectAssetSearchPageNumberSelector = (
  sessionId: string
): ((state: RootState) => number | undefined) =>
  createSelector(
    makeSelectAssetSearchSessionSelector(sessionId),
    (config) => config?.page
  );

export const makeSelectAssetSearchRowsPerPageSelector = (
  sessionId: string
): ((state: RootState) => number | undefined) =>
  createSelector(
    makeSelectAssetSearchSessionSelector(sessionId),
    (config) => config?.rowsPerPage
  );

export const makeSelectAssetSearchSelectedRowIndicesSelector = (
  sessionId: string
): ((state: RootState) => number[] | undefined) =>
  createSelector(
    makeSelectAssetSearchSessionSelector(sessionId),
    (config) => config?.selectedIndices
  );

export const makeSelectAssetSearchOperationStatusSelector = (
  sessionId: string
): ((state: RootState) => OperationStatus) =>
  createSelector(
    makeSelectAssetSearchSessionSelector(sessionId),
    (config) => config?.operationStatus || IDLE_OPERATION_STATUS
  );

export const makeSelectComplianceAssetTypesSelector = (
  sessionId: string
): ((state: RootState) => ApiAssetType[] | undefined) =>
  createSelector(
    makeSelectAssetComplianceSessionSelector(sessionId),
    (config) => config?.filterCriteria?.selectedAssetTypes
  );

export const makeSelectAssetComplianceTotalCountSelector = (
  sessionId: string
): ((state: RootState) => number | undefined) =>
  createSelector(
    makeSelectAssetComplianceSessionSelector(sessionId),
    (config) => config?.count
  );

export const makeSelectAssetCompliancePageNumberSelector = (
  sessionId: string
): ((state: RootState) => number) =>
  createSelector(
    makeSelectAssetComplianceSessionSelector(sessionId),
    (config) => config?.page || 0
  );

export const makeSelectAssetComplianceRowsPerPageSelector = (
  sessionId: string
): ((state: RootState) => number | undefined) =>
  createSelector(
    makeSelectAssetComplianceSessionSelector(sessionId),
    (config) => config?.rowsPerPage
  );

export const makeSelectAssetComplianceSortOrderSelector = (
  sessionId: string
): ((
  state: RootState
) => { name: string; direction: 'asc' | 'desc' } | undefined) =>
  createSelector(
    makeSelectAssetComplianceSessionSelector(sessionId),
    (config) => config?.sortOrder
  );

export const makeSelectAssetComplianceOperationStatusSelector = (
  sessionId: string
): ((state: RootState) => OperationStatus) =>
  createSelector(
    makeSelectAssetComplianceSessionSelector(sessionId),
    (config) => config?.operationStatus || IDLE_OPERATION_STATUS
  );
export const makeSelectSelectedAssetReport = (
  sessionId: string
): ((state: RootState) => AssignAssetItem[]) => {
  const selectedIndices =
    makeSelectAssetListSelectedRowIndicesSelector(sessionId);
  return createSelector(
    selectAllAssetReportItems,
    selectedIndices,
    (assets, selectedIndices) => {
      if (selectedIndices && assets) {
        const selectedAsset = assets.filter(
          (_, reportIndex) =>
            selectedIndices?.filter(
              (selectedIndex) => reportIndex === selectedIndex
            )?.length
        );
        return selectedAsset;
      }
      return [];
    }
  );
};
export const makeSelectContinuationTokenSelector = (
  sessionId: string
): ((state: RootState) => string | null) =>
  createSelector(
    makeSelectAssetReportSessionSelector(sessionId),
    (config) => config?.continuationToken || null
  );

export const makeSelectContinuationPageCacheSelector = (
  sessionId: string
): ((state: RootState) => ContinuationCache<AssetReportItem[]> | undefined) =>
  createSelector(makeSelectAssetReportSessionSelector(sessionId), (config) => {
    return config?.continuationCache || undefined;
  });
export const makeSelectAdvisorVehicleListContinuationPageCacheSelector = (
  sessionId: string
): ((
  state: RootState
) => ContinuationCache<AdvisorVehicleListItem[]> | undefined) =>
  createSelector(makeSelectAssetReportSessionSelector(sessionId), (config) => {
    return config?.continuationAdvisorVehicleCache || undefined;
  });
export const makeSelectPeripheralVehicleContinuationTokenSelector = (
  sessionId: string
): ((state: RootState) => string | null) =>
  createSelector(
    makeSelectPeripheralAssetReportSessionSelector(sessionId),
    (config) => config?.continuationToken || null
  );

export const makeSelectPendingVehicleContinuationTokenSelector = (
  sessionId: string
): ((state: RootState) => string | null) =>
  createSelector(
    makeSelectPendingVehicleSessionSelector(sessionId),
    (config) => config?.continuationToken || null
  );

export const makeSelectPeripheralVehicleContinuationPageCacheSelector = (
  sessionId: string
): ((state: RootState) => ContinuationCache<AssetReportItem[]> | undefined) =>
  createSelector(
    makeSelectPeripheralAssetReportSessionSelector(sessionId),
    (config) => {
      return config?.continuationCache || undefined;
    }
  );

export const makeSelectPendingVehicleContinuationPageCacheSelector = (
  sessionId: string
): ((
  state: RootState
) => ContinuationCache<PendingVehicleItem[]> | undefined) =>
  createSelector(
    makeSelectPendingVehicleSessionSelector(sessionId),
    (config) => {
      return config?.pendingVehiclesContinuationCache || undefined;
    }
  );

export const makeGetRemoteCommandStatus = (
  commandRequestType: AssetRemoteCommandRequestType
) =>
  createSelector(selectAssetsState, (assetsState) =>
    getOperationStatus(assetsState, commandRequestType)
  );

export const makeSelectSelectedVehicleIdsSelector = (
  sessionId: string
): ((state: RootState) => SelectedVehicleIds | undefined) =>
  createSelector(makeSelectAssetReportSessionSelector(sessionId), (config) => {
    return config?.selectedVehicleIds;
  });

export const makeGetAssetPowerStateStatus = (assetId: string) =>
  createSelector(
    (state: RootState) => selectAssetById(state, assetId),
    (asset): TelemetryStatus<RemoteCommandPowerIgnitionStatus> | null =>
      asset?.status?.powerState || null
  );

export const makeGetAssetGearStateStatus = (assetId: string) =>
  createSelector(
    (state: RootState) => selectAssetById(state, assetId),
    (asset): TelemetryStatus<GearStatus> | null =>
      asset?.status?.gearState || null
  );

export const makeGetAssetGearStateForMapStatus = (assetId: string) =>
  createSelector(
    (state: RootState) => selectAssetById(state, assetId),
    (asset): TelemetryStatus<GearStatus> | null =>
      asset?.status?.gearStateForMap || null
  );

export const makeGetAssetIdleState = (assetId: string) =>
  createSelector(
    (state: RootState) => selectAssetById(state, assetId),
    (asset): boolean => asset?.state?.vehicleIdling ?? false
  );

export const selectAssetLocationStatus = (assetId: string) =>
  createSelector(
    (state: RootState) => selectAssetById(state, assetId),
    (asset) => asset?.status?.location
  );

export const makeSelectAssetLocation = (assetId: string) =>
  createSelector(
    selectAssetLocationStatus(assetId),
    (locationStatus) => locationStatus?.value
  );

export const makeSelectAssetDisplayAddress = (assetId: string) =>
  createSelector(
    selectAssetLocationStatus(assetId),
    (locationStatus) => locationStatus?.displayAddress
  );

export const makeAssetStatusOperatorSelector = (assetId: string) => {
  return createSelector(selectAssetDetailsSessionConfigs, (config) => {
    return (
      (assetId && config?.[assetId]?.assetStatus?.operationStatus) ||
      IDLE_OPERATION_STATUS
    );
  });
};
export const makeSelectSelectedIncludedVehicleIdsSelector = (
  sessionId: string
): ((state: RootState) => Record<string, boolean> | undefined) =>
  createSelector(makeSelectAssetReportSessionSelector(sessionId), (config) => {
    return config?.includedVehicleIds;
  });

export const makeSelectExcludedVehicleIdsSelector = (
  sessionId: string
): ((state: RootState) => Record<string, boolean> | undefined) =>
  createSelector(makeSelectAssetReportSessionSelector(sessionId), (config) => {
    return config?.excludedVehicleIds;
  });

export const makeAssetIgnitionOperatorSelector = createSelector(
  selectAssetsState,
  (assetsState) =>
    getOperationStatus(assetsState, AssetRequestType.GET_ASSET_IGNITION_STATUS)
);

export const makeAssetIgnitionSelector = (assetId: string) =>
  createSelector(
    (state: RootState) => selectAssetById(state, assetId),
    (asset): AssetIgnitionStatus =>
      asset?.ignitionStatus || AssetIgnitionStatus.UNKNOWN
  );
export const makeSelectContinuationAdvisorvehiclePageCacheSelector = (
  sessionId: string
): ((
  state: RootState
) => ContinuationCache<AdvisorVehicleListItem[]> | undefined) =>
  createSelector(makeSelectAssetReportSessionSelector(sessionId), (config) => {
    return config?.continuationCache || undefined;
  });

export const selectActiveBatchIdByOrg = (
  organizationsId: string
): ((state: RootState) => string | null) =>
  createSelector(
    (state: RootState) => state.assets,
    (assetsState): string | null =>
      assetsState.activeBatches[organizationsId] || null
  );

export const makeSelectLocationMaskingEnabled = (assetId: string) =>
  createSelector(
    (state: RootState) => selectAssetById(state, assetId),
    (asset) => asset?.locationMaskingEnabled
  );
